/**
 * The layer manager's stacking rule: which `z-index` a layer gets, from the
 * `--z-*` tokens in `src/styles/tokens.css` and how deep it was opened.
 *
 * Every overlay asks here (through `useLayerZIndex` in
 * `src/hooks/useOverlayZIndex.ts`, or the recipe variables below for the
 * Chakra parts it positions itself); no component writes a z-index of its
 * own. Within one band the order is the token order the Ledger defines:
 * popover (30) under sheet (40) under dialog (50) under toast (60).
 *
 * Two bands, because the app still holds ~250 literal z-indexes in screens
 * that have not been restyled (TOKEN-MAP.md section 8b; between 1000 and 2050):
 *
 * - The page band holds sheets and dialogs opened from the page. It sits
 *   where Chakra put them before (about 1500), so the screens' own fixed
 *   panels, spotlights and Langy keep their places relative to a drawer.
 * - The floating band holds popovers, menus, selects and tooltips, and any
 *   layer opened from inside one of them. Each step of depth lifts a layer a
 *   whole band step above its parent, so a layer always sits above the layer
 *   it was opened from. It starts where the old depth counter did (2010 at
 *   depth one), above every literal a screen sets.
 *
 * The open order among siblings is Zag's layer stack (`--layer-index`), which
 * is also what makes Esc close only the topmost layer and a press outside
 * close a popover or a menu.
 *
 * Spec: specs/design-system/ledger-layers.feature
 * Deviation: docs/design-system/DEVIATIONS.md, L-01.
 */

export type LayerKind = "popover" | "sheet" | "dialog" | "toast";

const KIND_TOKEN: Record<LayerKind, string> = {
  popover: "--z-popover",
  sheet: "--z-sheet",
  dialog: "--z-dialog",
  toast: "--z-toast",
};

/**
 * The manager's three numbers, as custom properties on `:root` so every
 * layer, recipe and wrapper reads the same ones (registered in
 * `src/theme/ledgerRecipes.ts`). They are the band origins, not layer
 * positions: a layer's position is always one of these plus a `--z-*` token.
 */
export const layerStackProperties = {
  "--lw-layer-page-base": "1460",
  "--lw-layer-float-base": "1880",
  "--lw-layer-step": "100",
} as const;

/**
 * The z-index of a layer of `kind`, `depth` floating layers deep.
 *
 * `depth` 0 is a sheet or dialog opened from the page (the page band). A
 * popover is always at least depth 1: it is itself a floating layer. A sheet
 * or dialog opened from inside a floating layer takes that layer's depth plus
 * one, like a popover would.
 */
export function layerZIndex({
  kind,
  depth,
}: {
  kind: LayerKind;
  depth: number;
}): string {
  const token = `var(${KIND_TOKEN[kind]})`;
  if (kind === "toast") {
    // Above any depth a screen can reach: the toast region is never covered.
    return `calc(var(--lw-layer-float-base) + ${token} * var(--lw-layer-step))`;
  }
  if (depth <= 0) {
    return `calc(var(--lw-layer-page-base) + ${token})`;
  }
  return `calc(var(--lw-layer-float-base) + ${token} + ${depth} * var(--lw-layer-step))`;
}

/**
 * Chakra's own `zIndex` scale, re-pointed at the manager for the keys that
 * name a layer. Recipes of parts the app does not wrap (raw `Combobox`,
 * `HoverCard`, `Select`) read these; the content keys (`docked`, `dropdown`,
 * `sticky`, `banner`) stay Chakra's until the screens that use them move.
 */
export const layerZIndexTokens = {
  overlay: { value: layerZIndex({ kind: "dialog", depth: 0 }) },
  modal: { value: layerZIndex({ kind: "dialog", depth: 0 }) },
  popover: { value: layerZIndex({ kind: "popover", depth: 1 }) },
  tooltip: { value: layerZIndex({ kind: "popover", depth: 1 }) },
  toast: { value: layerZIndex({ kind: "toast", depth: 0 }) },
} as const;

/**
 * Every layer part Zag mounts and animates in and out. Not the triggers: a
 * closed menu's button is `data-state=closed` too, and must take presses.
 */
const LAYER_PARTS =
  ":is(:is([data-scope=dialog], [data-scope=drawer], [data-scope=popover], [data-scope=hover-card], [data-scope=menu], [data-scope=select], [data-scope=combobox], [data-scope=tooltip]):is([data-part=backdrop], [data-part=positioner], [data-part=content]), [data-scope=toast][data-part=root])";

/**
 * How a closing layer leaves.
 *
 * - A closed layer never takes a press: its backdrop, positioner and content
 *   stop catching the pointer the moment it starts to close, so a backdrop
 *   still fading out (or left behind) cannot swallow a click on the page.
 * - Under reduced motion it leaves at once. `tokens.css` shortens every
 *   animation to 0.01ms there, and Zag's presence, which waits for the exit
 *   animation's end before it unmounts, sometimes missed that end and left a
 *   closed layer mounted and exposed. With no exit animation at all, presence
 *   unmounts immediately.
 */
export const layerExitCss = {
  [`${LAYER_PARTS}[data-state=closed]`]: {
    pointerEvents: "none !important",
  },
  "@media (prefers-reduced-motion: reduce)": {
    [`${LAYER_PARTS}[data-state=closed]`]: {
      animationName: "none !important",
    },
  },
} as const;
